import { isMobile } from "react-device-detect"

import { aemAxiosInstance, apim } from "@/constants/api"
import {
  apimSubscriptionKey,
  apiBaseUrl,
  authorBaseUrl,
  PDP_END_POINT_SUBSCRIPTION_KEY,
  PLP_END_POINT,
  PLP_END_POINT_SUBSCRIPTION_KEY,
  EXCEPTIONAL_SITENAME,
  SSR_PLP_FL,
  searchPlpEndPoint,
} from "@/constants"
import { getUserPersona } from "@/utils/helper"
import qs from "qs"
import {
  addCompareProducts,
  clearProduct,
  updateCompareStatus,
} from "@/store/features/compareSlice"
import { store } from "@/store"
import { getConfig } from "@/constants/config"

const isServer = typeof window === "undefined"

const addCollectionsToEndPointIfNeeded = (url = "", brandName = "") => {
  let endPoint = url
  if (brandName?.toLowerCase() !== EXCEPTIONAL_SITENAME)
    endPoint = `${url}&collections=${brandName?.toLowerCase()}`
  return endPoint
}

const getListFromServer = async (
  profileName,
  persona,
  brandName = "",
  locale
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = addCollectionsToEndPointIfNeeded(
        `${apiBaseUrl}${PLP_END_POINT_SUBSCRIPTION_KEY}?profilename=${profileName}&persona=${persona}`,
        brandName
      )
      aemAxiosInstance.defaults.headers.common[
        "Ocp-Apim-Subscription-Key"
      ] = `${apimSubscriptionKey}`
      const { data: content } = await aemAxiosInstance({
        url,
        params: {
          fq: `language_s:("${locale}")`,
          fl: SSR_PLP_FL.join(","),
        },
      })
      resolve(content)
    } catch (error) {
      reject(error)
    }
  })
}

const getListFromClient = async (
  profileName,
  persona,
  accessToken,
  brandName = ""
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = addCollectionsToEndPointIfNeeded(
        `${authorBaseUrl}${PLP_END_POINT}?profilename=${profileName}&persona=${persona}`,
        brandName
      )
      aemAxiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${accessToken}`
      const { data: content } = await aemAxiosInstance({
        url,
      })
      resolve(content)
    } catch (error) {
      reject(error)
    }
  })
}

const getPlpParams = (obj, targetKey) => {
  let result = null
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      if (key === targetKey) {
        return obj[key]
      }

      if (typeof obj[key] === "object" && obj[key] !== null) {
        result = getPlpParams(obj[key], targetKey)

        if (result !== null) {
          return result
        }
      }
    }
  }
  return result
}

const getSearchItems = async (
  searchKey,
  accessToken,
  displayName = "SEARCH"
) => {
  return new Promise(async (resolve, reject) => {
    try {
      aemAxiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${accessToken}`
      const { data: content } = await aemAxiosInstance({
        url: `${authorBaseUrl}${PLP_END_POINT}?displayname=${displayName}&q=${searchKey}`,
      })
      resolve(content)
    } catch (error) {
      reject(error)
    }
  })
}

const formatListingData = async (data, stripResponse) => {
  return new Promise((resolve, reject) => {
    try {
      const persona = getUserPersona()
      const payload = {
        facets: data.facet_counts?.facet_fields,
        facetLabels: data.fusion?.facet_labels ?? [],
        grouped:
          data.fusion?.isCollapsePLP === "true" ||
          data.fusion?.isCollapsePLP === true,
        fusion: data.fusion,
        PlpSwatches: data.facet_counts?.Plp_Swatches ?? [],
      }

      const priceRange = payload.grouped
        ? data.facet_counts?.facet_ranges[
            `discountedPriceStartingAt.${persona}_d`
          ]
        : data.facet_counts?.facet_ranges[`priceList.${persona}.finalPrice_d`]
      if (priceRange) {
        const gap = priceRange.gap
        // eslint-disable-next-line camelcase
        payload.facets.Price_Range = priceRange.counts.map(item =>
          typeof item === "string"
            ? `[${parseInt(item)} TO ${parseInt(item) + gap}]`
            : item
        )
      }

      // mark multiselect
      payload.facets = formatMultiSelectFacets(data)
      // set color swatches
      payload.colors = data.fusion?.color_swatch_labels ?? {}

      if (data.response) {
        if (stripResponse && data.response.docs.length > 3) {
          payload.data = data.response.docs.slice(0, 3)
        } else {
          payload.data = data.response.docs
        }

        payload.total = data.response.numFound
        payload.start = data.response.start
        if (
          data.fusion?.isCollapsePLP === "true" ||
          data.fusion?.isCollapsePLP === true
        ) {
          payload.grouped = true
        }
      } else {
        payload.data = data.grouped[Object.keys(data.grouped)[0]].groups.map(
          doc => doc.doclist.docs[0]
        )
        payload.total = data.grouped[Object.keys(data.grouped)[0]].groups.length
        payload.start = 0
        payload.grouped = true
      }
      resolve(payload)
      // update
      //   ? dispatch({ type: GET_MORE_PRODUCTS, payload: data.response.docs })
      //   : dispatch({ type: GET_PRODUCTS, payload })

      // dispatch({ type: STOP_LOADING, payload: "plp" })
    } catch (error) {
      reject(error)
    }
  })
}

const formatMultiSelectFacets = data => {
  const facets = Object.entries(data.facet_counts?.facet_fields ?? {}).map(
    fields => ({ [fields[0]]: fields[1] })
  )
  let facetField = data?.fusion["facet.field"]
  if (facetField) {
    if (typeof facetField === "string") facetField = [facetField]
    facetField.forEach(item => {
      facets.forEach((filter, index) => {
        if (filter[item]) {
          const obj = {
            [`**${item}`]: filter[item],
          }
          facets.splice(index, 1, obj)
        }
      })
    })
  }
  const result = Object.assign({}, ...facets)
  return result
}

const getRowsInitialValue = () => {
  let rowsInitialValue = 30
  if (!isServer) {
    rowsInitialValue = isMobile
      ? 30
      : sessionStorage.rows &&
        JSON.parse(sessionStorage.rows)[window.location.pathname]
      ? JSON.parse(sessionStorage.rows)[window.location.pathname]
      : 30
  }
  return rowsInitialValue
}

const addToCompareProduct = async (
  sku = "",
  data = {},
  isBackOrder = false,
  slug
) => {
  try {
    store.dispatch(updateCompareStatus(true))

    const isBackOrderable = Boolean(
      isBackOrder || data?.RegionIsBackOrderable_b
    )
    const category =
      data?.Product_Category || data?.ProductLocalCategory_s || ""

    const state = store.getState()
    const existingCompareSkus =
      state.compare?.comparedProducts?.map(p => p.compareSku || p.sku_s) || []

    const compareResult = await fetchCompareDetails(
      sku,
      existingCompareSkus,
      category
    )

    const compareData =
      compareResult?.response?.docs?.find(item => item.sku_s === sku) || {}

    const compareLabels = compareResult?.fusion?.facet_labels

    const productData = {
      ...data,
      compareData,
      compareLabels,
    }

    store.dispatch(
      addCompareProducts({
        productData,
        backorder: isBackOrderable,
        compareSku: sku,
        slug,
      })
    )
  } catch (error) {
    console.error("Error adding product to compare:", error)
    store.dispatch(updateCompareStatus(false))
  }
}

const fetchCompareDetails = async (sku, compareSkus, category) => {
  const {
    general: { lwAppName = "" },
    internationalization: { language = "en" },
  } = await getConfig()
  const allSkus = [...compareSkus, sku].filter(Boolean)
  const encodedCategory = encodeURIComponent(category)
  const fq = [
    allSkus.length > 1 ? `sku_s:(${allSkus.join(" OR ")})` : `sku_s:(${sku})`,
    `ProductLocalCategory_s: "${encodedCategory}"`,
    `language_s: ("${language}")`,
  ]

  const res = await apim.get(searchPlpEndPoint, {
    params: {
      q: "*:*",
      fq: fq,
      compare: true,
      collections: lwAppName,
      profilename: `profile_${lwAppName}_Search`,
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: "repeat", encode: false })
    },
  })
  return res.data
}

export const getProductDataFromSkus = async (skus = [], category = "") => {
  try {
    const {
      general: { lwAppName = "" },
    } = await getConfig()

    if (!skus.length) return []

    const quotedSkus = skus.map(sku => `"${sku}"`).join(" ")
    const fq = `sku_s:(${quotedSkus})`

    // Fetch product data
    const { data: { response: { docs: productDocs = [] } = {} } = {} } =
      await apim.get(searchPlpEndPoint, {
        params: {
          fl: SSR_PLP_FL.join(","),
          q: "*:*",
          fq,
          collections: lwAppName,
          profilename: `profile_${lwAppName}_General`,
          rows: skus.length,
        },
        paramsSerializer: params =>
          qs.stringify(params, { arrayFormat: "repeat", encode: false }),
      })

    // Fetch compare data
    const compareResponse = await fetchCompareDetails("", skus, category)
    const compareDocs = compareResponse?.response?.docs || []
    const compareLabels = compareResponse?.fusion?.facet_labels || {}

    // Helper to map compare data
    const mapProduct = product => {
      const matchingCompareData = compareDocs.find(
        c => c.sku_s === product.sku_s
      )

      return {
        ...product,
        compareData: matchingCompareData,
        compareLabels,
      }
    }

    return productDocs.map(mapProduct)
  } catch (error) {
    console.error("Error fetching product data:", error)
    return []
  }
}

const removeFromCompareProduct = sku => {
  store.dispatch(clearProduct(sku))
}

const getProductDetailsFromServer = async (
  profileName,
  collections,
  slug,
  locale
) => {
  return new Promise(async (resolve, reject) => {
    try {
      aemAxiosInstance.defaults.headers.common[
        "Ocp-Apim-Subscription-Key"
      ] = `${apimSubscriptionKey}`
      const response = await aemAxiosInstance({
        url: `${apiBaseUrl}${PDP_END_POINT_SUBSCRIPTION_KEY}?profilename=${profileName}&collections=${collections}&slug=${slug}&fq=language_s:"${locale}"`,
      })
      const { data: { response: { docs = [] } = {} } = {} } = response
      const { data: { fusion: { masterVariantDataResponse = [] } = {} } = {} } =
        response
      resolve(docs[0] ? { ...docs[0], ...{ masterVariantDataResponse } } : {})
    } catch (error) {
      reject(error)
    }
  })
}

const getFilterLabel = (showFilter, staticTexts) => {
  if (showFilter) {
    return staticTexts.hideFilter
  }

  if (window.innerWidth <= 1023) {
    return staticTexts.filterSort
  }

  return staticTexts.showFilter
}

const getFilterBarProps = ({
  totalResults,
  staticTexts,
  updatedKeyWord,
  handleSearchOnchange,
  onSearch,
  typeAheadResults,
  showFilter,
  addAnalyticsForShowHideFilter,
  sorts,
  getSortItemBasedOnURL,
  optionsPreparation,
  handleSort,
  getSortOptionByValue,
  handleDatalayer,
  type,
  page,
  searchPlaceholder,
  isFilterBarSticky,
  setIsFilterBarSticky,
}) => ({
  resultsCount: `${totalResults} ${staticTexts?.results}`,
  mod: "white",

  searchInput: isFilterBarSticky
    ? {
        id: "search-result-input-sticky-scroll",
        value: updatedKeyWord,
        label: `${staticTexts?.searchBrand} ${searchPlaceholder}`,
        onChange: e => {
          handleSearchOnchange(e.target.value)
        },
        onKeyDown: e =>
          (e.key === "Enter" || e.keyCode === 13) && onSearch(updatedKeyWord),
        searchCTA: {
          text: staticTexts.searchText,
          onClick: () => {
            updatedKeyWord?.length > 0 && onSearch(updatedKeyWord)
          },
        },
        results: {
          options: updatedKeyWord?.length > 2 ? typeAheadResults : [],
          onOptionSubmit: e => {
            onSearch(e.value)
          },
        },
        clearCTA: {
          onClick: () => {
            handleSearchOnchange("")
          },
        },
      }
    : undefined,
  onStickyChange: isSticky => {
    setIsFilterBarSticky(isSticky)
  },

  filtersCTA: {
    label: getFilterLabel(showFilter, staticTexts),
    onClick: () => addAnalyticsForShowHideFilter(showFilter),
  },

  sortDropdownProps: {
    defaultValue: getSortItemBasedOnURL(staticTexts, {
      name: staticTexts.featured,
    }).query,
    options: optionsPreparation(sorts),
    className: `gbh-data-layer`,
    onSelect: e => {
      handleSort(getSortOptionByValue(sorts, e))
      handleDatalayer(getSortOptionByValue(sorts, e), {
        type,
        page,
      })
    },
    placeholder: staticTexts.sort,
  },
})

export {
  getListFromServer,
  getListFromClient,
  getPlpParams,
  getSearchItems,
  formatListingData,
  getRowsInitialValue,
  addToCompareProduct,
  removeFromCompareProduct,
  getProductDetailsFromServer,
  getFilterBarProps,
}
